import { useEffect, useRef, useCallback } from 'react';
import { Canvas, Gradient } from 'fabric';
import { fitCanvasToContainer, parseGradientToFabric } from './utils/canvas-helpers';

/**
 * Fabric.js canvas wrapper component.
 * Handles initialization, responsive sizing, and background management.
 */
export const CanvasWorkspace = ({
  fabricRef,
  canvasWidth,
  canvasHeight,
  background,
  onObjectModified,
  onSelectionChange,
  formatLabel,
}) => {
  const canvasElRef = useRef(null);
  const containerRef = useRef(null);

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (!canvasElRef.current || fabricRef.current) return;

    const canvas = new Canvas(canvasElRef.current, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: '#FFFFFF',
      preserveObjectStacking: true,
      selection: false, // No multi-select for v1
    });

    // Mobile-friendly touch targets
    canvas.getObjects().forEach(obj => {
      obj.set({
        cornerSize: 12,
        touchCornerSize: 40,
        cornerPadding: 10,
        cornerColor: '#FFFFFF',
        cornerStrokeColor: '#000000',
        borderColor: '#FFFFFF',
        transparentCorners: false,
      });
    });

    // Wire events
    canvas.on('object:modified', () => onObjectModified?.());
    canvas.on('object:added', () => onObjectModified?.());
    canvas.on('object:removed', () => onObjectModified?.());
    canvas.on('selection:created', (e) => onSelectionChange?.(e.selected?.[0] || null));
    canvas.on('selection:updated', (e) => onSelectionChange?.(e.selected?.[0] || null));
    canvas.on('selection:cleared', () => onSelectionChange?.(null));

    fabricRef.current = canvas;

    return () => {
      canvas.dispose();
      fabricRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle responsive sizing
  const updateSize = useCallback(() => {
    const container = containerRef.current;
    const canvas = fabricRef.current;
    if (!container || !canvas) return;

    const { displayWidth, displayHeight } = fitCanvasToContainer(
      canvasWidth,
      canvasHeight,
      container.clientWidth,
      container.clientHeight
    );

    // Set canvas element to display dimensions (fits in container)
    // Zoom maps logical coordinates (e.g. 1080x1920) to display pixels
    canvas.setDimensions({ width: displayWidth, height: displayHeight });
    canvas.setZoom(displayWidth / canvasWidth);
    canvas.renderAll();
  }, [fabricRef, canvasWidth, canvasHeight]);

  useEffect(() => {
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [updateSize]);

  // Update canvas dimensions when aspect ratio changes
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.setDimensions({ width: canvasWidth, height: canvasHeight });
    updateSize();
  }, [canvasWidth, canvasHeight, fabricRef, updateSize]);

  // Apply background
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !background) return;

    if (background.type === 'solid') {
      canvas.backgroundColor = background.value;
      canvas.renderAll();
    } else if (background.type === 'gradient') {
      const parsed = parseGradientToFabric(background.value, canvasWidth, canvasHeight);
      if (parsed) {
        canvas.backgroundColor = new Gradient({
          type: 'linear',
          coords: parsed.coords,
          colorStops: parsed.colorStops,
        });
        canvas.renderAll();
      }
    } else if (background.type === 'image' && background.value) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const fabricImg = new (require('fabric').FabricImage)(img);
        fabricImg.scaleToWidth(canvasWidth);
        if (fabricImg.getScaledHeight() < canvasHeight) {
          fabricImg.scaleToHeight(canvasHeight);
        }
        fabricImg.set({
          originX: 'center',
          originY: 'center',
          left: canvasWidth / 2,
          top: canvasHeight / 2,
        });
        canvas.backgroundImage = fabricImg;
        canvas.renderAll();
      };
      img.src = background.value;
    }
  }, [background, fabricRef, canvasWidth, canvasHeight]);

  return (
    <div
      ref={containerRef}
      className="flex-1 flex items-center justify-center overflow-hidden p-4 md:p-8 relative"
      style={{ touchAction: 'none' }}
    >
      <canvas ref={canvasElRef} />
      {formatLabel && (
        <p className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] tracking-widest uppercase opacity-25 font-display select-none pointer-events-none whitespace-nowrap">
          {formatLabel}
        </p>
      )}
    </div>
  );
};
